// @ts-strict-ignore
import { delay, inject, injectWithTransform, singleton } from "tsyringe";
import {
  AssetLoader,
  LocalStorage,
  Logger,
  LoggerTransformer,
  SessionStorage,
} from "@identity/utils";
import {
  DataWarehouseEventType,
  DataWarehouseService,
  InstantConfigService,
  MetricReporterService,
} from "@identity/services";
import { WINDOW } from "@identity/symbols";

@singleton()
export class LiveConnect {
  private storage;
  private storageConfig: CachingStrategyConfig;

  private partnerName = "liveConnect";
  private liveConnectScriptUrl = "https://b-code.liadm.com/a-07ev.min.js";
  private fallbackQf = "0.3";

  private live_connect_tracking_events = {
    LIVE_CONNECT_STARTED: "live_connect_started",
    LIVE_CONNECT_CACHED: "live_connect_cached",
    LIVE_CONNECT_RESPONDED_UUID: "live_connect_responded_uuid",
  };

  private idConfigMapping: IdConfig[] = [
    { id: "unifiedId", name: `${this.partnerName}-unifiedId` },
    {
      id: "sha2",
      name: this.partnerName,
      params: { qf: "0.3", resolve: "sha2" },
    },
    {
      id: "md5",
      name: `${this.partnerName}-md5`,
      params: { qf: "0.3", resolve: "md5" },
    },
    {
      id: "sha1",
      name: `${this.partnerName}-sha1`,
      params: { qf: "0.3", resolve: "sha1" },
    },
  ];

  constructor(
    @inject(AssetLoader) private readonly assetLoader: AssetLoader,
    @inject(WINDOW) private readonly window: Window,
    @inject(delay(() => InstantConfigService))
    private readonly instantConfigService: InstantConfigService,
    @inject(DataWarehouseService)
    private readonly dataWarehouse: DataWarehouseService,
    @injectWithTransform(Logger, LoggerTransformer, "liveConnect")
    private readonly logger: Logger,
    @inject(MetricReporterService)
    private readonly metricReporter: MetricReporterService,
  ) {}

  private isEnabled(): boolean {
    const { ready, blocking, coppa, optOut } = this.window.fandomCmp;
    return (
      ready &&
      !blocking &&
      !coppa &&
      !optOut &&
      this.instantConfigService.get("icLiveConnect") &&
      this.instantConfigService.get("icLiveConnectCachingStrategy") &&
      this.instantConfigService.get("icIdentityPartners")
    );
  }

  async init(): Promise<void> {
    if (!this.isEnabled()) {
      this.logger.info("disabled");
      return;
    }

    this.setupStorage();

    if (this.shouldLoadScript()) {
      this.logger.info("loading");
      this.dataWarehouse.track(
        DataWarehouseEventType.loadTime,
        this.live_connect_tracking_events.LIVE_CONNECT_STARTED,
      );
      this.assetLoader
        .loadScript(this.liveConnectScriptUrl, "text/javascript", false)
        .then(() => {
          this.logger.info("loaded");
          this.resolveAndTrackIds();
          this.logEvent("live-connect-requested");
        });
    } else {
      this.dataWarehouse.track(
        DataWarehouseEventType.loadTime,
        this.live_connect_tracking_events.LIVE_CONNECT_CACHED,
      );
      this.logger.info(
        `already loaded and available in ${this.storageConfig.type}Storage`,
      );
    }
  }

  resolveAndTrackIds(): void {
    if (!this.window.liQ) {
      this.logger.warn("window.liQ not available for tracking");
      return;
    }
    const customQf = this.instantConfigService
      .get<number>("icLiveConnectQf")
      ?.toString();
    this.window.liQ.resolve(
      (result) => {
        this.trackIds(result);
      },
      (err) => {
        console.error(err);
      },
      {
        qf: customQf || this.fallbackQf,
        resolve: this.idConfigMapping.map((config) => config.id),
      },
    );
  }

  trackIds(liQResponse: LiQResolveResponse): void {
    this.logger.info("resolve response:", liQResponse);

    Object.keys(liQResponse).forEach((key) => {
      if (!liQResponse[key]) {
        return;
      }

      const trackingKeyName = this.getTrackingKeyName(key);

      if (this.isAvailableInStorage(trackingKeyName)) {
        return;
      }

      if (key === "unifiedId") {
        this.dataWarehouse.track(
          DataWarehouseEventType.loadTime,
          this.live_connect_tracking_events.LIVE_CONNECT_RESPONDED_UUID,
        );
      }

      const partnerIdentityId = liQResponse[key];

      this.logger.info(`${key}: ${partnerIdentityId}`);

      if (!partnerIdentityId) {
        return;
      }

      this.storage.setItem(
        trackingKeyName,
        partnerIdentityId,
        this.storageConfig.ttl,
      );

      this.dataWarehouse.track(DataWarehouseEventType.identity, {
        partner_name: trackingKeyName,
        partner_identity_id: partnerIdentityId,
      });
    });
  }

  getTrackingKeyName(key: string): string {
    return this.idConfigMapping.find((config) => config.id === key)?.name;
  }

  setupStorage(): void {
    this.storageConfig = this.instantConfigService.get<CachingStrategyConfig>(
      "icLiveConnectCachingStrategy",
    );

    if (this.storageConfig.type === "local") {
      this.storage = new LocalStorage(this.window);
    } else {
      this.storage = new SessionStorage(this.window);
    }
  }

  shouldLoadScript(): boolean {
    if (!this.storageConfig) {
      return true;
    }

    let shouldLoadScript = false;

    for (const param of this.storageConfig.mandatoryParams) {
      const storageKey = this.idConfigMapping.find(
        (config) => config.id === param,
      )?.name;

      if (!this.isAvailableInStorage(storageKey)) {
        shouldLoadScript = true;
        break;
      }
    }

    return shouldLoadScript;
  }

  isAvailableInStorage(key: string): boolean {
    return !!this.storage.getItem(key);
  }

  logEvent(event: string): void {
    this.metricReporter.send(event);
  }
}
